// The part-rig bundle's engine: decode once at the neutral pose, then re-lift and recolour.
// Notes: ~/comments/aos-threejs-poc/PartRigPipeline_composite_decode_once.md

import type * as THREE from "three";
import { createFrameSlot } from "../inference/splatFrameSlot.js";
import type { OGSBranch } from "../graph/nodes/OGSBranch.ts";
import type { NodeTensor } from "../graph/types.ts";
import { partEnabled, partPbr, type RenderPlan } from "./renderPlan.ts";
import type { DrivenLifters } from "./drivenLifter.ts";
import type { OgsPipeline, PipelineDebug, PipelineReady } from "./types.ts";
import { createSilentLogger, type OgsLogger } from "./ogsLog.ts";

/** One branch's decoder outputs for a pass. */
type BranchUvs = { geom_uv: NodeTensor; color_uv: NodeTensor } | null;

/** The terminal render node, structurally — so a test injects a stub. */
export interface RendererLike {
  evaluate(frame: unknown, inbound: Record<string, NodeTensor>): Promise<unknown>;
  readonly lastCount: number;
  setSourceEnabled(name: string, on: boolean): void;
}

/** The one frame slot a driven pose and a recolour share (inference/splatFrameSlot.js). */
export interface FrameSlotLike {
  readonly inFlight: boolean;
  readonly appearanceStale: boolean;
  readonly driveOwned: boolean;
  acquire(): boolean;
  release(): void;
  requestAppearance(now: number): boolean;
  consumeAppearance(now: number): boolean;
  consumeSettledAppearance(now: number): boolean;
  setDriveOwned(owned: boolean): void;
}

/** Whoever poses the part rig. Installed by the body drive; absent until a bundle ships one. */
export interface PartPoser {
  /** Pose every branch at `t`, returning the branches whose ANCHOR moved. Synchronous. */
  pose(t: number): OGSBranch[] | void;
  /** Re-skin the HEAD only, for a face frame that moved no body bone. */
  poseFace?(): void;
}

/** Milliseconds of the last driven frame, mutated in place — never rebuilt per frame. */
export interface FramePerf {
  deformMs: number;
  apprMs: number;
  /** submitMs + paceMs — kept so the existing readouts still mean what they did. */
  liftMs: number;
  /** Encoding and submitting the lift: real work, fixed by making the lift cheaper. */
  submitMs: number;
  /** Waiting for the app to DRAW the submitted lift (the `pace` handshake). More
   *  than one app frame here is a pipelining bug, not a cost. */
  paceMs: number;
  frameMs: number;
}

export interface PartRigPipelineOptions {
  branches: OGSBranch[];
  renderer: RendererLike;
  /** The camera IN THE AVATAR'S FRAME, re-resolved per pass (render/localFrameCamera). */
  camera: () => THREE.PerspectiveCamera;
  /** Splat visibility, owned by the render-phase gate. */
  setVisible: (visible: boolean) => void;
  slot?: FrameSlotLike;
  /** The wrapped lifters the renderer's sources use — for the cull counts and the driving flag. */
  lifters?: DrivenLifters | null;
  /** False while the render phase is not `rendering`, or after teardown. */
  isActive?: () => boolean;
  log?: OgsLogger;
  worldScale?: number;
  /** Awaited after every present, INSIDE the frame slot. Supply it when the
   *  present is EXTERNAL (an on-device overlay draws, nothing reads back): with
   *  no readback nothing else bounds the GPU queue, so the decode loop runs tens
   *  of frames ahead and the page's own frames starve — measured on the legacy
   *  path at 9 fps / 17.5 poses per second unpaced against 18.9 / 18.9 paced.
   *  The overlay's `nextFrame()` resolves once a frame was presented, so at most
   *  one pose is queued past what is on screen. */
  pace?: (() => Promise<void>) | null;
}

/** How long a busy slot is waited on before a region toggle re-presents. */
export const REGION_RETRY_MS = 50;

export class PartRigPipeline implements OgsPipeline {
  readonly kind = "part-rig" as const;

  private readonly branches: OGSBranch[];
  private readonly renderer: RendererLike;
  private readonly camera: () => THREE.PerspectiveCamera;
  private readonly pace: (() => Promise<void>) | null;
  private readonly setVisibleFn: (visible: boolean) => void;
  private readonly slot: FrameSlotLike;
  private readonly isActive: () => boolean;
  private readonly log: OgsLogger;
  private readonly worldScale: number;
  private readonly lifters: DrivenLifters | null;
  /** A `head_cond: local_disp` head re-decodes on EVERY driven frame — see poseAndPresent. */
  private readonly dispHead: OGSBranch | null;
  private readonly dispHeadRig: Float32Array | null;
  /** Whoever poses the part rig, installed by the body drive. */
  private poser: PartPoser | null = null;

  /** Which regions render, by branch name. Every region starts on. */
  private readonly regionOn: Map<string, boolean>;
  private neutralDone = false;
  private presentedCount = 0;
  /** The clip time of the pose that actually LANDED — never the caller's clock. */
  private posedTime: number | null = null;
  private readonly framePerf: FramePerf = { deformMs: 0, apprMs: 0, liftMs: 0, submitMs: 0, paceMs: 0, frameMs: 0 };
  private disposed = false;
  private retry: ReturnType<typeof setTimeout> | null = null;

  constructor(options: PartRigPipelineOptions) {
    this.branches = options.branches;
    this.renderer = options.renderer;
    this.camera = options.camera;
    this.pace = options.pace ?? null;
    this.setVisibleFn = options.setVisible;
    this.slot = options.slot ?? createFrameSlot();
    this.isActive = options.isActive ?? (() => true);
    this.log = options.log ?? createSilentLogger();
    this.worldScale = options.worldScale ?? 1;
    this.lifters = options.lifters ?? null;
    this.regionOn = new Map(this.branches.map((branch) => [branch.name, true]));
    this.dispHead = this.branches.find((branch) => branch.isLocalDisp) ?? null;
    this.dispHeadRig = this.dispHead?.neutralRig() ?? null;
  }

  /** Decode every region at its own reference pose and present once. */
  async init(): Promise<PipelineReady> {
    await this.composite("full");
    return {
      count: this.presentedCount,
      lines: [`neutral render presented ${this.presentedCount} splats `
        + `(${this.branches.length} branches, worldScale=${this.worldScale})`],
    };
  }

  /** This pipeline decodes once; a rig push has nothing to re-decode. */
  setRig(): void {}

  /** The view moved. A recolour asked while a frame is presenting is KEPT, not dropped. */
  runAppearance(): void {
    if (this.disposed) return;
    if (this.slot.requestAppearance(performance.now())) void this.composite("appearance");
  }

  setVisible(visible: boolean): void {
    this.setVisibleFn(visible);
  }

  /** The LIGHT moved — re-lift and re-shade at the decoder outputs already in
   *  hand. No decode: a PBR branch's material is not a function of the light, so
   *  only the shade stage's own two passes have anything new to say, and
   *  `runAppearance` would pay for a full appr pass to learn nothing.
   *
   *  A no-op while anything else holds the frame slot, which is what keeps it
   *  free during a walk: a driven frame is already re-lifting every frame and
   *  carries the new light itself. This is for the avatar that MOVES without
   *  animating — dragged on the gizmo, or walked under a follow camera that
   *  leaves the local view unchanged — where nothing else would re-present and
   *  the character keeps the lighting of the spot it left. */
  relight(): void {
    if (this.disposed || !this.neutralDone) return;
    if (!this.isActive() || !this.slot.acquire()) return;
    void (async () => {
      try {
        this.presentedCount = await this.presentFrame(this.branches.map((b) => b.uvOutputs()));
      } catch (error) {
        this.log.error("relight failed:", error);
      } finally {
        this.slot.release();
      }
    })();
  }

  /** Is a light change worth a re-present? Only a PBR branch renders under the scene's light. */
  get followsLight(): boolean {
    return this.branches.some((branch) => !!branch.lifter?.pbrShade);
  }

  /** Blueprint edit: the region toggles and the per-region PBR switch. */
  applyPlan(next: RenderPlan, _prev: RenderPlan | null): void {
    if (this.disposed) return;
    let changed = false;
    for (const branch of this.branches) {
      const on = partEnabled(next, branch.name);
      if (this.regionOn.get(branch.name) !== on) {
        this.regionOn.set(branch.name, on);
        this.renderer.setSourceEnabled(branch.name, on);
        this.log.info(`branch '${branch.name}' ${on ? "on" : "off"} (blueprint)`);
        changed = true;
      }
      // A shade stage exists only where the BUNDLE said the appr emits a
      // material, so this reaches nothing on an RGB region (hair) — the same
      // "both the bundle and the blueprint must allow it" rule the face-drive
      // masks follow. Live: `setFrame` packs it on the next frame.
      const shade = branch.lifter?.pbrShade;
      if (shade) {
        const lit = partPbr(next, branch.name);
        if (shade.isLit !== lit) {
          shade.setLit(lit);
          this.log.info(`branch '${branch.name}' PBR ${lit ? "on" : "albedo only"} (blueprint)`);
          changed = true;
        }
      }
    }
    if (changed) this.presentRegions();
  }

  /** The clip time of the pose that is ACTUALLY ON SCREEN — the root is placed at this. */
  get landedTime(): number | null { return this.posedTime; }

  /** Install the body drive's poser, or clear it. */
  setPoser(poser: PartPoser | null): void {
    this.poser = poser;
  }

  /**
   * The body drive's per-frame call. `t` is the clip time; `null` means NOBODY poses the
   * rig this frame, which is the slot's cue to run a pending recolour on its own.
   *
   * Synchronous and returns whether the frame was TAKEN — the present runs after.
   */
  driveAt(t: number | null): boolean {
    if (this.disposed) return false;
    this.slot.setDriveOwned(t != null);
    if (t == null) {
      if (this.slot.appearanceStale) void this.composite("appearance");
      return false;
    }
    if (!this.isActive() || !this.poser || !this.slot.acquire()) return false;
    void this.poseAndPresent(t);
    return true;
  }

  /** A face frame moved the facial joints and nothing else: re-skin the head alone.
   *  False when the BODY drive owns the rig — its next frame carries the bones free. */
  driveFace(): boolean {
    if (this.disposed || this.slot.driveOwned) return false;
    if (!this.isActive() || !this.poser?.poseFace || !this.slot.acquire()) return false;
    void this.faceOnlyPresent();
    return true;
  }

  /** Does this branch produce its OWN vertices, so the part rig must NOT pose it?
   *
   *  The question is the DEFORM and nothing else. A branch with one writes its
   *  own verts and the body reaches it through `bodySkin` (or, failing that, an
   *  anchor delta); a branch without one is posed like every other part, through
   *  `driver.verts.deform(name)`, which skins it against the body rig and is the
   *  ordinary path.
   *
   *  IT IS NOT `isLocalDisp`, and that mistake shipped. A local-disp head looked
   *  like the textbook "live branch" — it owns a displacement map — so keying on
   *  it seemed right. But on a synthetic-face bundle (myra-pbr: `rig: none`) NO
   *  branch has a deform, the head was being part-skinned perfectly well, and
   *  calling it live took it off that working path: skinned by the body it span
   *  around the scene, and left to the anchor delta it stopped rotating at all,
   *  because `applyPartRigPose` routes a live branch away from the part skin
   *  either way. Both arms were worse than doing nothing.
   *
   *  It is also NOT `headTeethEngine.isLive`, which asks whether a branch
   *  RE-DECODES per rig (`trunk`/`fused`) — a decode-budget question whose own
   *  comment excludes `local_disp` for its own reasons. Three notions of "live"
   *  exist in this repo and they answer three different questions; this one is
   *  about who poses the vertices. */
  isLive(branch: OGSBranch): boolean {
    return branch.hasDeform;
  }

  debug(): PipelineDebug {
    return {
      kind: this.kind,
      branches: this.branches.map((branch) => branch.name),
      regions: Object.fromEntries(this.regionOn),
      count: this.presentedCount,
      neutralDone: this.neutralDone,
      worldScale: this.worldScale,
      driven: !!this.poser,
      drivenTime: this.posedTime,
      culled: this.lifters?.culled ?? 0,
      perf: this.framePerf,
      dispHead: this.dispHead?.name ?? null,
      // PER-BRANCH SUBMIT COST + how its decoder output arrived. `submitMs` is
      // 212 ms of a 276 ms pose on myra-pbr and the whole of the visible lag, so
      // the next question is WHICH branch and WHY — a `cpu` io mode means both UV
      // maps are re-uploaded from the CPU on every present, which on a 13-channel
      // PBR branch is megabytes a pose.
      lift: Object.fromEntries(this.branches.map((b) => {
        const l = (b as { lifter?: { lastLiftSubmitMs?: number; packedMode?: boolean } }).lifter;
        const geom = (b as { lastGeomUv?: { loc?: string } }).lastGeomUv;
        return [b.name, { submitMs: l?.lastLiftSubmitMs ?? null, io: geom?.loc ?? null }];
      })),
      // WHEN each branch's verts were last set, against the clip. During a walk
      // every branch must carry the SAME time; whichever one does not is the
      // head-lags-the-body bug, measured rather than argued.
      posedAt: Object.fromEntries(this.branches.map((b) => [b.name, b.lastPosedAt])),
    };
  }

  /** Drops this pipeline's own timers only — the caller owns the branches and the scene. */
  dispose(): void {
    this.disposed = true;
    this.poser = null;
    if (this.retry !== null) { clearTimeout(this.retry); this.retry = null; }
  }

  /** A toggle that lands mid-decode must still reach the screen: nothing else re-presents
   *  a static avatar, so without this retry it reads as dead until the next camera move. */
  private presentRegions(): void {
    if (this.disposed) return;
    if (this.slot.inFlight) {
      this.retry = setTimeout(() => { this.retry = null; this.presentRegions(); }, REGION_RETRY_MS);
      return;
    }
    void this.composite(this.neutralDone ? "appearance" : "full");
  }

  private async composite(mode: "full" | "appearance"): Promise<void> {
    if (this.disposed || !this.isActive()) return;
    if (mode === "appearance" && !this.neutralDone) return;
    if (!this.slot.acquire()) return;
    try {
      // Consumed BEFORE decoding: a view that moves mid-decode is asked for again.
      this.slot.consumeAppearance(performance.now());
      const uvs: BranchUvs[] = [];
      for (const branch of this.branches) {
        // A region switched off decodes nothing and the renderer skips its source,
        // so its splats leave the frame rather than freezing in it.
        if (!this.regionOn.get(branch.name)) { uvs.push(null); continue; }
        // A region switched back ON has no cached geom to recolour: the first pass
        // after it returns is a full decode, whatever the mode asked for.
        const view = this.camera();
        uvs.push(mode === "full" || !branch.lastGeomUv
          ? await branch.renderFull(branch.neutralRig(), view)
          : await branch.renderAppearance(view));
        if (this.disposed) return;
      }
      this.presentedCount = await this.presentFrame(uvs);
      if (mode === "full") this.neutralDone = true;
    } catch (error) {
      // Loud: a pass that throws leaves the last frame on screen, which reads as working.
      this.log.error(`${mode} pass failed:`, error);
    } finally {
      this.slot.release();
    }
  }

  /** GEOM only, every driven frame: the disp head's expression IS the vertex displacement.
   *  Appr rides the settle throttle like every other branch — it is camera-dirty, and
   *  OGSAvatar feeds it the map geom decoded from, so a late pass keeps this expression. */
  private async decodeDispHead(): Promise<void> {
    const head = this.dispHead;
    if (!head || !this.dispHeadRig || !this.regionOn.get(head.name)) return;
    await head.runGeom(this.dispHeadRig);
  }

  /** Re-run appr for every live region at the current view — the frozen colour is stale. */
  private async recolour(): Promise<void> {
    for (const branch of this.branches) {
      if (!this.regionOn.get(branch.name) || !branch.lastGeomUv) continue;
      await branch.renderAppearance(this.camera());
      if (this.disposed) return;
    }
  }

  /** One driven frame: pose, decode what must re-decode, re-lift, present. */
  private async poseAndPresent(clipTime: number): Promise<void> {
    this.lifters?.setDriving(true);
    const started = performance.now();
    try {
      this.poser?.pose(clipTime);
      const deformEnd = performance.now();
      await this.decodeDispHead();
      if (this.disposed) return;
      // The view settled (or the colour is overdue): appr re-runs in THIS frame, at this pose.
      if (this.slot.consumeSettledAppearance(performance.now())) await this.recolour();
      if (this.disposed) return;
      const apprEnd = performance.now();
      this.presentedCount = await this.presentFrame(this.branches.map((branch) => branch.uvOutputs()));
      const presented = performance.now();
      // Mutated, never rebuilt: this runs on every driven frame.
      this.framePerf.deformMs = deformEnd - started;
      this.framePerf.apprMs = apprEnd - deformEnd;
      this.framePerf.liftMs = presented - apprEnd;
      this.framePerf.frameMs = presented - started;
      this.posedTime = clipTime;
    } catch (error) {
      this.log.error("driven frame failed:", error);
    } finally {
      this.slot.release();
    }
  }

  /** A face frame: only the head was re-skinned, so only the head is re-decoded. */
  private async faceOnlyPresent(): Promise<void> {
    this.lifters?.setDriving(true);
    try {
      this.poser?.poseFace?.();
      await this.decodeDispHead();
      if (this.disposed) return;
      if (this.slot.consumeSettledAppearance(performance.now())) await this.recolour();
      if (this.disposed) return;
      this.presentedCount = await this.presentFrame(this.branches.map((branch) => branch.uvOutputs()));
    } catch (error) {
      this.log.error("face frame failed:", error);
    } finally {
      this.slot.release();
    }
  }

  /** ONE terminal pass: the renderer lifts every source it got UVs for, presents once. */
  private async presentFrame(uvs: readonly BranchUvs[]): Promise<number> {
    const t0 = performance.now();
    const inbound: Record<string, NodeTensor> = {};
    this.branches.forEach((branch, index) => {
      const out = uvs[index];
      if (!out) return;
      const [geomPort, colorPort] = branch.outputs.map((port) => port.name);
      inbound[geomPort] = out.geom_uv;
      inbound[colorPort] = out.color_uv;
    });
    await this.renderer.evaluate({ rig: null, camera: this.camera(), rigEpoch: 0, cameraEpoch: 0 }, inbound);
    // SUBMIT AND WAIT ARE DIFFERENT COSTS WITH OPPOSITE FIXES, and `liftMs`
    // reported their sum — 204 ms of it on myra-pbr, which reads equally as "the
    // lift is slow" and as "we wait four drawn frames per pose". Submitting is
    // work; pacing is the 1:1 backpressure handshake, and if it costs more than
    // ONE app frame the handshake itself is the bug.
    const submitted = performance.now();
    // Backpressure on the external present — see `pace` in the options.
    if (this.pace) await this.pace();
    this.framePerf.submitMs = submitted - t0;
    this.framePerf.paceMs = performance.now() - submitted;
    return this.renderer.lastCount;
  }
}
